import React, { ReactNode, useState } from 'react';
import {
  actionLinkClassName,
  actionPrimaryButtonClassName,
} from '@core/theme/actionTheme.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

interface EditableSectionProps {
  title: string;
  children: ReactNode;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void | Promise<void>;
  editButtonText?: string;
  showSaveButton?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const EditableSection: React.FC<EditableSectionProps> = ({
  title,
  children,
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  editButtonText = 'Edit',
  showSaveButton = true,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Don't allow collapsing while editing
  const canCollapse = collapsible && !isEditing;
  const showContent = !collapsible || !isCollapsed || isEditing;

  const handleToggleCollapse = () => {
    if (canCollapse) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div
          className={`flex items-center gap-2 ${canCollapse ? 'cursor-pointer select-none' : ''}`}
          onClick={handleToggleCollapse}
        >
          {collapsible && (
            <span className="text-gray-400">
              {isCollapsed && !isEditing ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </span>
          )}
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>
        {!isEditing && onEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Expand if collapsed when clicking edit
              if (isCollapsed) {
                setIsCollapsed(false);
              }
              onEdit();
            }}
            className={`flex items-center gap-2 text-sm font-medium ${actionLinkClassName}`}
          >
            <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
            {editButtonText}
          </button>
        )}
        {isEditing && showSaveButton && (
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="text-sm font-medium text-gray-600 hover:text-gray-700"
            >
              Cancel
            </button>
            {onSave && (
              <button
                onClick={onSave}
                className={`rounded-md px-4 py-2 text-sm font-medium ${actionPrimaryButtonClassName}`}
              >
                Save
              </button>
            )}
          </div>
        )}
      </div>
      {showContent && children}
    </div>
  );
};

export default EditableSection;
